import { faker } from "@faker-js/faker";
import { v4 } from "uuid";

import { getMockMarket, getMockMarketProfile } from "../market";

export function getDemoCurrency(): "NPR" | "AED" | "USD" {
  return getMockMarketProfile().currency;
}

export function getDemoLocale(): "ne" | "en" {
  return getMockMarketProfile().locale;
}

/**
 * Generate a UUID
 */
export function createId(): string {
  return v4({
    random: Uint8Array.from({ length: 16 }, () =>
      faker.number.int({ min: 0, max: 255 }),
    ),
  });
}

/**
 * Generate current timestamp as ISO string
 */
export function createTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Generate a timestamp from the past
 */
export function createPastTimestamp(daysAgo: number = 30): string {
  if (daysAgo <= 0) {
    return new Date().toISOString();
  }

  return faker.date.recent({ days: daysAgo }).toISOString();
}

/**
 * Generate a timestamp within the last `hoursAgo` hours. Use this where the
 * value must stay inside a messaging window (e.g. a contact's last inbound
 * message) - `createPastTimestamp`'s day-granularity range routinely falls
 * outside the 24h window and would block the composer.
 */
export function createRecentTimestamp(hoursAgo: number = 6): string {
  const minutesAgo = faker.number.int({ min: 5, max: hoursAgo * 60 });

  return new Date(Date.now() - minutesAgo * 60 * 1000).toISOString();
}

/** The way every ledger endpoint orders rows: latest on top. */
export function newestFirst<T extends { createdAt: string }>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/**
 * Generate a date object from the past
 */
export function createPastDate(daysAgo: number = 30): Date {
  if (daysAgo <= 0) {
    return new Date();
  }

  return faker.date.recent({ days: daysAgo });
}

export function createPhoneNumber(): string {
  const market = getMockMarket();

  if (market === "nepal") {
    const operator = pickRandom(["980", "981", "982", "984", "985", "986"]);
    const number = randomInt(1000000, 9999999).toString().padStart(7, "0");

    return `+977${operator}${number}`;
  }

  if (market === "usa") {
    const areaCode = pickRandom([
      "202",
      "212",
      "305",
      "312",
      "415",
      "617",
      "646",
    ]);
    const number = randomInt(1000000, 9999999).toString().padStart(7, "0");

    return `+1${areaCode}${number}`;
  }

  // UAE mobile: +971 5X XXX XXXX
  const operators = ["50", "52", "54", "55", "56", "58"];
  const operator = pickRandom(operators);
  const number = randomInt(1000000, 9999999);
  const numStr = number.toString().padStart(7, "0");

  return `+971${operator}${numStr}`;
}

/**
 * Generate a random email
 */
export function createEmail(firstName?: string, lastName?: string): string {
  return faker.internet.email({
    firstName: firstName ?? undefined,
    lastName: lastName ?? undefined,
  });
}

export function createAvatarUrl(): string {
  return "";
}

/**
 * Generate a random image URL
 */
export function createImageUrl(
  width: number = 400,
  height: number = 300,
): string {
  return faker.image.urlPicsumPhotos({ width, height });
}

/**
 * Generate a random color hex code
 */
export function createColor(): string {
  return faker.color.rgb({ format: "hex" });
}

/**
 * Generate a random price string
 */
export function createPrice(min: number = 10, max: number = 500): string {
  return faker.commerce.price({ min, max, dec: 2 });
}

/**
 * Pick a random element from array
 */
export function pickRandom<T>(array: T[]): T {
  return faker.helpers.arrayElement(array);
}

/**
 * Pick multiple random elements from array
 */
export function pickMultiple<T>(
  array: T[],
  min: number = 1,
  max: number = 3,
): T[] {
  const count = faker.number.int({ min, max: Math.min(max, array.length) });

  return faker.helpers.arrayElements(array, count);
}

/**
 * Generate a random boolean with optional probability
 */
export function randomBool(probability: number = 0.5): boolean {
  return faker.datatype.boolean({ probability });
}

/**
 * Generate a random integer
 */
export function randomInt(min: number, max: number): number {
  return faker.number.int({ min, max });
}

/**
 * Dubai/UAE Name Pools for multicultural population
 */
const ARAB_MALE_NAMES = [
  "Ahmed",
  "Mohammed",
  "Omar",
  "Khalid",
  "Abdullah",
  "Hassan",
  "Ali",
  "Faisal",
  "Yousef",
  "Rashid",
];
const ARAB_FEMALE_NAMES = [
  "Fatima",
  "Aisha",
  "Mariam",
  "Noura",
  "Sara",
  "Layla",
  "Hana",
  "Reem",
  "Zahra",
  "Salma",
];
const EUROPEAN_MALE_NAMES = [
  "James",
  "Michael",
  "David",
  "Robert",
  "Thomas",
  "Daniel",
  "Alexander",
  "William",
];
const EUROPEAN_FEMALE_NAMES = [
  "Emma",
  "Sophia",
  "Olivia",
  "Isabella",
  "Charlotte",
  "Emily",
  "Grace",
  "Victoria",
];
const ASIAN_MALE_NAMES = [
  "Rahul",
  "Arjun",
  "Ravi",
  "Amit",
  "Raj",
  "Carlos",
  "Jose",
  "Miguel",
];
const ASIAN_FEMALE_NAMES = [
  "Priya",
  "Ananya",
  "Divya",
  "Sofia",
  "Maria",
  "Ana",
  "Jasmine",
  "Mei",
];

const ARAB_LAST_NAMES = [
  "Al-Mansoori",
  "Al-Falasi",
  "Al-Nuaimi",
  "Al-Suwaidi",
  "Al-Hammadi",
  "Al-Mazrouei",
  "Al-Hashmi",
  "Al-Shamsi",
  "Al-Dhaheri",
  "Al-Ketbi",
];
const EUROPEAN_LAST_NAMES = [
  "Smith",
  "Johnson",
  "Williams",
  "Anderson",
  "Taylor",
  "Martinez",
  "Garcia",
];
const ASIAN_LAST_NAMES = [
  "Kumar",
  "Sharma",
  "Patel",
  "Singh",
  "Fernandez",
  "Santos",
  "Lee",
  "Chen",
];

/**
 * Nepali (romanized) name pools - used when the demo currency is NPR.
 */
const NEPALI_MALE_NAMES = [
  "Aarav",
  "Bishal",
  "Sujan",
  "Nabin",
  "Prakash",
  "Rohan",
  "Anish",
  "Bibek",
  "Sandesh",
  "Kiran",
];
const NEPALI_FEMALE_NAMES = [
  "Sita",
  "Anjali",
  "Puja",
  "Sunita",
  "Bina",
  "Muna",
  "Sarita",
  "Nisha",
  "Aasha",
  "Manisha",
];
const NEPALI_LAST_NAMES = [
  "Sharma",
  "Gurung",
  "Shrestha",
  "Thapa",
  "Adhikari",
  "Rai",
  "Tamang",
  "Karki",
  "Poudel",
  "Magar",
];

const AMERICAN_MALE_NAMES = [
  "Ethan",
  "Noah",
  "Liam",
  "Mason",
  "Lucas",
  "Benjamin",
  "Henry",
  "Marcus",
  "Daniel",
  "Jack",
];
const AMERICAN_FEMALE_NAMES = [
  "Olivia",
  "Emma",
  "Ava",
  "Sophia",
  "Mia",
  "Harper",
  "Chloe",
  "Grace",
  "Maya",
  "Sofia",
];
const AMERICAN_LAST_NAMES = [
  "Carter",
  "Brooks",
  "Williams",
  "Johnson",
  "Miller",
  "Davis",
  "Garcia",
  "Ramirez",
  "Patel",
  "Kim",
];

/**
 * Generate a romanized Nepali name.
 */
export function generateNepaliName(gender?: "male" | "female"): {
  firstName: string;
  lastName: string;
} {
  const randomGender = gender ?? (randomBool() ? "male" : "female");
  const firstName =
    randomGender === "male"
      ? pickRandom(NEPALI_MALE_NAMES)
      : pickRandom(NEPALI_FEMALE_NAMES);
  const lastName = pickRandom(NEPALI_LAST_NAMES);

  return { firstName, lastName };
}

export function generateAmericanName(gender?: "male" | "female"): {
  firstName: string;
  lastName: string;
} {
  const randomGender = gender ?? (randomBool() ? "male" : "female");
  const firstName =
    randomGender === "male"
      ? pickRandom(AMERICAN_MALE_NAMES)
      : pickRandom(AMERICAN_FEMALE_NAMES);

  return { firstName, lastName: pickRandom(AMERICAN_LAST_NAMES) };
}

export function generateLocalName(gender?: "male" | "female"): {
  firstName: string;
  lastName: string;
} {
  const market = getMockMarket();

  if (market === "nepal") return generateNepaliName(gender);
  if (market === "uae") return generateDubaiName(gender);

  return generateAmericanName(gender);
}

/**
 * Generate a UAE-focused name mix while retaining Dubai's international feel.
 */
export function generateDubaiName(gender?: "male" | "female"): {
  firstName: string;
  lastName: string;
} {
  const randomGender = gender ?? (randomBool() ? "male" : "female");
  // Use the seeded faker PRNG (not Math.random) so generated names are
  // deterministic across server boots - required for stable visual baselines.
  const ethnicityRand = faker.number.float({ min: 0, max: 1 });

  let firstName: string;
  let lastName: string;

  if (ethnicityRand < 0.6) {
    firstName =
      randomGender === "male"
        ? pickRandom(ARAB_MALE_NAMES)
        : pickRandom(ARAB_FEMALE_NAMES);
    lastName = pickRandom(ARAB_LAST_NAMES);
  } else if (ethnicityRand < 0.75) {
    firstName =
      randomGender === "male"
        ? pickRandom(EUROPEAN_MALE_NAMES)
        : pickRandom(EUROPEAN_FEMALE_NAMES);
    lastName = pickRandom(EUROPEAN_LAST_NAMES);
  } else {
    firstName =
      randomGender === "male"
        ? pickRandom(ASIAN_MALE_NAMES)
        : pickRandom(ASIAN_FEMALE_NAMES);
    lastName = pickRandom(ASIAN_LAST_NAMES);
  }

  return { firstName, lastName };
}
